/**
 * Chattr.jsx — FINAL v3 (World-class UI)
 * Place at: src/pages/Chattr.jsx
 *
 * LOGIC: Completely unchanged
 * VISUAL: Full redesign
 *   - Dark mode works properly across all views
 *   - "You are talking to" moved into chat as system message
 *   - Partner bubbles use proper theme-aware colors (not black)
 *   - Report button redesigned — subtle ⋮ menu, not a flag
 *   - Interest grid redesigned — pill tags, horizontally scrollable on mobile
 *   - Queued view — animated concentric rings, not just a pulsing circle
 *   - Chat header — minimal, like WhatsApp/iMessage
 *   - System messages inline (connected, left, disconnected)
 *   - Input bar — glassmorphism style, elevated feel
 *   - All transitions smooth
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";
import { useChattrSocket } from "../hooks/useChattrSocket";

const INTERESTS = [
  { value: "Anxiety",         emoji: "🌀", label: "Anxiety" },
  { value: "Depression",      emoji: "🌧️", label: "Depression" },
  { value: "Stress",          emoji: "💨", label: "Stress" },
  { value: "Loneliness",      emoji: "🌙", label: "Loneliness" },
  { value: "Relationships",   emoji: "💞", label: "Relationships" },
  { value: "Self-Esteem",     emoji: "🌱", label: "Self-Esteem" },
  { value: "Burnout",         emoji: "🕯️", label: "Burnout" },
  { value: "Mindfulness",     emoji: "🍃", label: "Mindfulness" },
  { value: "Motivation",      emoji: "⚡", label: "Motivation" },
  { value: "Grief",           emoji: "🕊️", label: "Grief" },
  { value: "Career Pressure", emoji: "📚", label: "Career Pressure" },
  { value: "Breakup",         emoji: "💔", label: "Breakup" },
  { value: "Parenting",       emoji: "🌻", label: "Parenting" },
  { value: "Bullying",        emoji: "🛡️", label: "Bullying" },
  { value: "Bollywood",       emoji: "🎬", label: "Bollywood" },
  { value: "Cricket",         emoji: "🏏", label: "Cricket" },
  { value: "Music",           emoji: "🎵", label: "Music" },
  { value: "Gaming",          emoji: "🎮", label: "Gaming" },
  { value: "Football",        emoji: "⚽", label: "Football" },
  { value: "Politics",        emoji: "🗳️", label: "Politics" },
];

const BAD_WORDS = [
  "fuck","shit","bastard","bitch","asshole","dick","pussy","cunt",
  "motherfucker","fucker","chutiya","madarchod","behenchod","bhenchod",
  "gandu","harami","randi","saala","mc","bc","lund","gaand","bhosdike",
  "nigger","faggot","retard","whore","slut","rape",
];

function containsBadWord(text) {
  const lower = text.toLowerCase().replace(/[^a-z0-9\u0900-\u097F ]/g, "");
  return BAD_WORDS.some(w => lower.includes(w));
}

// ─── PROMISES ─────────────────────────────────────────────────────────────────
const PROMISES = [
  "I promise to honor your story and always remain respectful.",
  "I pledge to protect this safe space and always remain kind.",
  "I promise to choose empathy over judgment and always remain patient.",
  "I pledge to be the light in someone's day and always remain gentle.",
  "I promise to listen with an open heart and always remain present.",
  "I pledge to lift others up and always remain supportive.",
  "I promise to respect your privacy and always remain trustworthy.",
  "I pledge to keep this conversation safe and always remain mindful.",
  "I promise to value our shared time and always remain sincere.",
  "I pledge to offer warmth to a stranger and always remain compassionate.",
];

const PROMISE_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

function shouldShowPromise() {
  const last = localStorage.getItem("chattr_last_promise");
  if (!last) return true;
  return Date.now() - parseInt(last, 10) > PROMISE_COOLDOWN_MS;
}

function markPromiseSeen() {
  localStorage.setItem("chattr_last_promise", Date.now().toString());
}

function getRandomPromise() {
  return PROMISES[Math.floor(Math.random() * PROMISES.length)];
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const Chattr = () => {
  const navigate    = useNavigate();
  const currentUser = auth.currentUser;
  const [theme, setTheme]                   = useState(localStorage.getItem("theme") || "light-theme");
  const [showInsightMenu, setShowInsightMenu] = useState(false);
  const hideTimeout = useRef(null);
  const [nickname,         setNickname]         = useState("");
  const [selectedInterest, setSelectedInterest] = useState("");
  const [view,             setView]             = useState("home");
  const [showPromise,      setShowPromise]       = useState(false);
  const [currentPromise,   setCurrentPromise]    = useState("");
  const pendingMatchRef = useRef(null);

  const {
    status, queueStatus, matchData, messages,
    partnerTyping, error,
    joinQueue, cancelQueue, sendMessage, sendTyping, sendReaction, leaveRoom, resetError,
  } = useChattrSocket(currentUser);

  useEffect(() => {
    if (queueStatus === "matched" && matchData) {
      // Show promise modal first if cooldown expired, else go straight to chat
      if (shouldShowPromise()) {
        pendingMatchRef.current = matchData;
        setCurrentPromise(getRandomPromise());
        setShowPromise(true);
      } else {
        setView("chat");
      }
    }
    else if (queueStatus === "queued")  setView("queued");
    else if (queueStatus === "idle")    setView("home");
    else if (queueStatus === "timeout") setView("home");
  }, [queueStatus, matchData]);

  const handlePromiseAccept = () => {
    markPromiseSeen();
    setShowPromise(false);
    setView("chat");
  };

  useEffect(() => { document.body.className = theme; }, [theme]);

  const toggleTheme = () => {
    const themes = ["light-theme","dark-theme","zen-theme"];
    const next = themes[(themes.indexOf(theme)+1)%themes.length];
    setTheme(next); localStorage.setItem("theme",next); document.body.className=next;
  };
  const handleLogout      = async () => { await signOut(auth); navigate("/"); };
  const handleInsightEnter = () => { clearTimeout(hideTimeout.current); setShowInsightMenu(true); };
  const handleInsightLeave = () => { hideTimeout.current = setTimeout(() => setShowInsightMenu(false), 150); };
  const handleStart = () => {
    if (!nickname.trim())   { alert("Please enter a nickname"); return; }
    if (!selectedInterest)  { alert("Please select a topic");   return; }
    joinQueue(nickname.trim(), selectedInterest);
  };
  const handleLeave = () => { leaveRoom(); setView("home"); };

  const isDark = theme === "dark-theme";

  return (
    <div className="dashboard-container">
      <nav className="navbar">
        <div className="navbar-left">
          <Link to="/dashboard" className="nav-item">Home</Link>
          <Link to="/resources">Resources</Link>
          <Link to="/community">Community</Link>
          <Link to="/feed" className="nav-item">Feed</Link>
          <Link to="/chatbot">Chat with Saksham</Link>
          <div className="nav-dropdown" onMouseEnter={handleInsightEnter} onMouseLeave={handleInsightLeave}>
            <span className="nav-item">Insight ▾</span>
            {showInsightMenu && (
              <div className="dropdown-menu">
                <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                <Link to="/insight" className="dropdown-link">Look at Report</Link>
              </div>
            )}
          </div>
          <Link to="/chattr" className="nav-item active">Chattr</Link>
        </div>
        <div className="navbar-center">
          <span className="healhub-brand"><span className="hug-icon">💖🌿</span> HealHub</span>
          <Link to="/about" className="nav-item">About Us</Link>
        </div>
        <div className="navbar-right">
          <button onClick={toggleTheme} className="theme-switcher-btn">
            {theme==="light-theme"?<MdOutlineLightMode/>:theme==="dark-theme"?<MdModeNight/>:<MdFilterVintage/>}
          </button>
          <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24}/></Link>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </nav>

      <div className="cx-page">
        {/* Status dot — only show when not connected */}
        {status !== "connected" && (
          <div className="cx-status-bar">
            <div className={`cx-dot cx-dot--${status}`}/>
            <span>{status === "connecting" ? "Connecting…" : "Reconnecting…"}</span>
          </div>
        )}

        {error && (
          <div className="cx-error-banner">
            <span>{error}</span>
            <button onClick={resetError}>×</button>
          </div>
        )}

        {view === "home" && (
          <HomeView
            nickname={nickname} setNickname={setNickname}
            selectedInterest={selectedInterest} setSelectedInterest={setSelectedInterest}
            onStart={handleStart} disabled={status !== "connected"} isDark={isDark}
          />
        )}
        {view === "queued" && (
          <QueuedView interest={selectedInterest} onCancel={() => { cancelQueue(); setView("home"); }} isDark={isDark}/>
        )}
        {view === "chat" && matchData && (
          <ChatView
            matchData={matchData} messages={messages}
            partnerTyping={partnerTyping}
            onSend={sendMessage} onTyping={sendTyping}
            onReact={sendReaction}
            onLeave={handleLeave} isDark={isDark}
          />
        )}
      </div>

      {/* ── Footer ── */}
      <div className="cx-footer">
        HealHub is a mental wellness platform. Please be respectful and kind.
        This space is anonymous, but not unaccountable.
      </div>

      {/* ── Promise modal — shown once per hour before chat starts ── */}
      {showPromise && (
        <div className="cx-promise-overlay">
          <div className="cx-promise-modal">
            <div className="cx-promise-icon">🤝</div>
            <p className="cx-promise-label">Before you begin</p>
            <blockquote className="cx-promise-text">
              "{currentPromise}"
            </blockquote>
            <p className="cx-promise-sub">
              Tap below to enter the conversation.
            </p>
            <button className="cx-promise-btn" onClick={handlePromiseAccept}>
              I Promise
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

// ─── HOME VIEW ────────────────────────────────────────────────────────────────
function HomeView({ nickname, setNickname, selectedInterest, setSelectedInterest, onStart, disabled, isDark }) {
  const canStart = !disabled && nickname.trim() && selectedInterest;
  return (
    <div className="cx-card cx-home">
      {/* Hero */}
      <div className="cx-hero">
        <div className="cx-hero-icon">
          <span>💬</span>
          <div className="cx-hero-ring"/>
          <div className="cx-hero-ring cx-hero-ring--2"/>
        </div>
        <h1 className="cx-title">Chattr</h1>
        <p className="cx-subtitle">
          Talk anonymously with someone who gets it.
          No judgment. No history. Just a real conversation.
        </p>
      </div>

      {/* Nickname */}
      <div className="cx-field">
        <label className="cx-label">YOUR NICKNAME</label>
        <input
          className="cx-input"
          placeholder="What should we call you?"
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          maxLength={30}
          onKeyDown={e => e.key === "Enter" && onStart()}
          autoComplete="off"
        />
      </div>

      {/* Topic */}
      <div className="cx-field">
        <label className="cx-label">WHAT DO YOU WANT TO TALK ABOUT?</label>
        <div className="cx-pills-wrap">
          <div className="cx-pills">
            {INTERESTS.map(({ value, emoji, label }) => (
              <button
                key={value}
                className={`cx-pill${selectedInterest === value ? " cx-pill--active" : ""}`}
                onClick={() => setSelectedInterest(value)}
              >
                <span className="cx-pill-emoji">{emoji}</span>
                <span>{label}</span>
              </button>
            ))}
            {/* Custom interest pill */}
            <CustomInterestPill
              selectedInterest={selectedInterest}
              setSelectedInterest={setSelectedInterest}
            />
          </div>
        </div>
      </div>

      {/* CTA */}
      <button className={`cx-btn-primary${canStart ? "" : " cx-btn-primary--disabled"}`} onClick={onStart} disabled={!canStart}>
        {disabled ? "Connecting…" : "Find Someone to Talk To →"}
      </button>

      {/* Share */}
      <div className="cx-share-row">
        <span className="cx-share-text">Know someone who could use this?</span>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(
            `Try Chattr on HealHub — talk anonymously about ${selectedInterest||"what's on your mind"}. https://heal-hub-healing-tfa4.vercel.app/chattr`
          )}`}
          target="_blank" rel="noopener noreferrer"
        >
          <button className="cx-btn-whatsapp">📤 Share on WhatsApp</button>
        </a>
      </div>
    </div>
  );
}

// ─── QUEUED VIEW ──────────────────────────────────────────────────────────────
function QueuedView({ interest, onCancel, isDark }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed(e => e+1), 1000);
    return () => clearInterval(t);
  }, []);

  const interestMeta = INTERESTS.find(i => i.value === interest) || { emoji: "💬", label: interest };

  return (
    <div className="cx-card cx-queued animate-in">
      <div className="cx-radar">
        <div className="cx-radar-ring cx-radar-ring--1"/>
        <div className="cx-radar-ring cx-radar-ring--2"/>
        <div className="cx-radar-ring cx-radar-ring--3"/>
        <div className="cx-radar-core">{interestMeta.emoji}</div>
      </div>
      <h2 className="cx-queued-title">Looking for a match</h2>
      <p className="cx-queued-sub">
        Finding someone interested in <strong>{interestMeta.label}</strong>
      </p>
      <div className="cx-queued-timer">
        <div className="cx-timer-bar">
          <div className="cx-timer-fill" style={{ width: `${Math.min((elapsed/30)*100,100)}%` }}/>
        </div>
        <span className="cx-timer-text">{elapsed}s · times out at 30s</span>
      </div>
      <button className="cx-btn-ghost" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// ─── CHAT VIEW ────────────────────────────────────────────────────────────────
function ChatView({ matchData, messages, partnerTyping, onSend, onTyping, onReact, onLeave, isDark }) {
  const [input,            setInput]           = useState("");
  const [showLeaveConfirm, setShowLeaveConfirm]= useState(false);
  const [showReportModal,  setShowReportModal] = useState(false);
  const [reportReason,     setReportReason]    = useState("");
  const [reportSent,       setReportSent]      = useState(false);
  const [blockedWarning,   setBlockedWarning]  = useState(false);
  const [showMenu,         setShowMenu]        = useState(false);
  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const typingSentRef   = useRef(false);
  const typingStopTimer = useRef(null);
  const menuRef         = useRef(null);

  const partnerLeft  = matchData?.partnerLeft;
  const interest     = matchData?.interest || "";
  const interestMeta = INTERESTS.find(i => i.value === interest) || { emoji: "💬", label: interest };

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, partnerTyping]);
  useEffect(() => { if (!partnerLeft) inputRef.current?.focus(); }, [partnerLeft]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  const handleInputChange = e => {
    setInput(e.target.value);
    setBlockedWarning(false);
    if (!typingSentRef.current) { onTyping(true); typingSentRef.current = true; }
    clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => { onTyping(false); typingSentRef.current = false; }, 1500);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || partnerLeft) return;
    if (containsBadWord(text)) {
      setBlockedWarning(true);
      setTimeout(() => setBlockedWarning(false), 3000);
      return;
    }
    onSend(text);
    setInput("");
    clearTimeout(typingStopTimer.current);
    onTyping(false);
    typingSentRef.current = false;
  };

  const handleKeyDown = e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleReport = () => {
    if (!reportReason) return;
    console.log("Report:", { reportedNickname: matchData.partnerNickname, reason: reportReason });
    setReportSent(true);
    setTimeout(() => { setShowReportModal(false); setReportSent(false); setReportReason(""); }, 2000);
  };

  return (
    <div className="cx-chat-wrap">
      {/* ── Minimal header (like WhatsApp) ── */}
      <div className="cx-chat-header">
        <div className="cx-chat-avatar">
          <span>{interestMeta.emoji}</span>
          <div className="cx-online-dot"/>
        </div>
        <div className="cx-chat-info">
          <div className="cx-chat-name">{matchData.partnerNickname}</div>
          <div className="cx-chat-meta">{interestMeta.label} · Anonymous</div>
        </div>
        {/* ⋮ menu */}
        <div className="cx-menu-wrap" ref={menuRef}>
          <button className="cx-menu-btn" onClick={() => setShowMenu(s => !s)} aria-label="Options">
            ⋮
          </button>
          {showMenu && (
            <div className="cx-dropdown">
              <button className="cx-dropdown-item cx-dropdown-item--danger" onClick={() => { setShowMenu(false); setShowReportModal(true); }}>
                Report user
              </button>
              <button className="cx-dropdown-item" onClick={() => { setShowMenu(false); setShowLeaveConfirm(true); }}>
                Leave chat
              </button>
            </div>
          )}
        </div>
        <button className="cx-leave-btn" onClick={() => setShowLeaveConfirm(true)}>Leave</button>
      </div>

      {/* ── Messages ── */}
      <div className="cx-messages">
        {/* System: connected */}
        <div className="cx-system-msg">
          <span>💙 You're now connected with <strong>{matchData.partnerNickname}</strong></span>
          <span className="cx-system-sub">This is a safe space · be kind</span>
        </div>

        {messages.map(msg => (
          <MessageBubble key={msg.id} msg={msg} partnerNickname={matchData.partnerNickname} onReact={onReact}/>
        ))}

        {/* Typing */}
        {partnerTyping && !partnerLeft && (
          <div className="cx-msg-row cx-msg-row--other">
            <div className="cx-avatar-sm">{matchData.partnerNickname[0].toUpperCase()}</div>
            <div className="cx-typing-bubble">
              <span className="cx-dot-bounce" style={{ animationDelay: "0s" }}/>
              <span className="cx-dot-bounce" style={{ animationDelay: "0.15s" }}/>
              <span className="cx-dot-bounce" style={{ animationDelay: "0.3s" }}/>
            </div>
          </div>
        )}

        {/* System: partner left */}
        {partnerLeft && (
          <div className="cx-system-msg cx-system-msg--end">
            <span>{matchData.partnerLeftReason === "disconnected" ? "📡" : "👋"} <strong>{matchData.partnerNickname}</strong> {matchData.partnerLeftReason === "disconnected" ? "lost connection" : "left the chat"}</span>
            <span className="cx-system-sub">{matchData.partnerLeftReason === "disconnected" ? "They may reconnect shortly." : "Hope the conversation helped. Take care."}</span>
            <button className="cx-btn-primary cx-btn-new-match" onClick={onLeave}>Find Someone New</button>
          </div>
        )}

        <div ref={messagesEndRef}/>
      </div>

      {/* ── Bad word warning ── */}
      {blockedWarning && (
        <div className="cx-blocked-warning">
          ⚠️ Please keep this a safe space — that language isn't allowed here.
        </div>
      )}

      {/* ── Input bar ── */}
      {!partnerLeft && (
        <div className="cx-input-bar">
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Write something kind…"
            rows={1}
            className={`cx-textarea${blockedWarning ? " cx-textarea--blocked" : ""}`}
          />
          <button className={`cx-send-btn${input.trim() ? " cx-send-btn--active" : ""}`} onClick={handleSend} disabled={!input.trim()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Leave modal ── */}
      {showLeaveConfirm && (
        <Modal onClose={() => setShowLeaveConfirm(false)}>
          <div className="cx-modal-icon">👋</div>
          <h3 className="cx-modal-title">Leave this chat?</h3>
          <p className="cx-modal-body">{matchData.partnerNickname} will be notified that you've left.</p>
          <div className="cx-modal-actions">
            <button className="cx-btn-ghost" onClick={() => setShowLeaveConfirm(false)}>Stay</button>
            <button className="cx-btn-danger" onClick={() => { setShowLeaveConfirm(false); onLeave(); }}>Leave</button>
          </div>
        </Modal>
      )}

      {/* ── Report modal ── */}
      {showReportModal && (
        <Modal onClose={() => { setShowReportModal(false); setReportReason(""); setReportSent(false); }}>
          {reportSent ? (
            <>
              <div className="cx-modal-icon">✅</div>
              <h3 className="cx-modal-title">Report submitted</h3>
              <p className="cx-modal-body">Thank you for keeping HealHub safe. We'll review this shortly.</p>
            </>
          ) : (
            <>
              <h3 className="cx-modal-title" style={{ marginTop: 0 }}>Report {matchData.partnerNickname}</h3>
              <p className="cx-modal-body">What's happening?</p>
              <div className="cx-report-options">
                {["Harassment or bullying","Inappropriate sexual content","Hate speech or discrimination","Threatening language","Spam or fake account","Other"].map(reason => (
                  <button key={reason} className={`cx-report-opt${reportReason===reason?" cx-report-opt--active":""}`} onClick={() => setReportReason(reason)}>
                    {reason}
                  </button>
                ))}
              </div>
              <button className={`cx-btn-danger${reportReason?"":" cx-btn-danger--disabled"}`} onClick={handleReport} disabled={!reportReason} style={{ width:"100%", marginTop:12 }}>
                Submit Report
              </button>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── CUSTOM INTEREST PILL ────────────────────────────────────────────────────
// Lets user type any custom topic not in the preset list
function CustomInterestPill({ selectedInterest, setSelectedInterest }) {
  const [editing,     setEditing]     = useState(false);
  const [customValue, setCustomValue] = useState("");
  const inputRef = useRef(null);

  // Is the currently selected interest a custom one (not in preset list)?
  const isCustomActive = selectedInterest && !INTERESTS.find(i => i.value === selectedInterest);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleConfirm = () => {
    const val = customValue.trim();
    if (val) setSelectedInterest(val);
    setEditing(false);
    setCustomValue("");
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") { e.preventDefault(); handleConfirm(); }
    if (e.key === "Escape") { setEditing(false); setCustomValue(""); }
  };

  if (editing) {
    return (
      <div className="cx-custom-pill-input-wrap">
        <input
          ref={inputRef}
          className="cx-custom-pill-input"
          value={customValue}
          onChange={e => setCustomValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleConfirm}
          placeholder="Type a topic…"
          maxLength={30}
        />
      </div>
    );
  }

  return (
    <button
      className={`cx-pill cx-pill--custom${isCustomActive ? " cx-pill--active" : ""}`}
      onClick={() => {
        if (isCustomActive) {
          // Click again to re-edit
          setCustomValue(selectedInterest);
          setEditing(true);
        } else {
          setEditing(true);
        }
      }}
      title="Add a custom topic"
    >
      <span className="cx-pill-emoji">✏️</span>
      <span>{isCustomActive ? selectedInterest : "Other…"}</span>
    </button>
  );
}

// ─── MESSAGE BUBBLE ───────────────────────────────────────────────────────────
const REACTION_EMOJIS = ["❤️", "😂", "😮", "😢", "👍", "🙏"];

function MessageBubble({ msg, partnerNickname, onReact }) {
  const [showReactions, setShowReactions] = useState(false);
  const hoverTimerRef = useRef(null);

  // msg.myReaction    = reaction I sent on this message
  // msg.theirReaction = reaction partner sent on this message (received via WS)
  const myReaction    = msg.myReaction    || null;
  const theirReaction = msg.theirReaction || null;

  const handleReactionClick = (emoji) => {
    // Toggle: click same emoji to remove, different emoji to change
    const next = myReaction === emoji ? null : emoji;
    onReact?.(msg.id, next); // sends to server + updates message in parent
    setShowReactions(false);
  };

  return (
    <div
      className={`cx-msg-row${msg.isSelf ? " cx-msg-row--self" : " cx-msg-row--other"}`}
      onMouseEnter={() => {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => setShowReactions(true), 400);
      }}
      onMouseLeave={() => {
        clearTimeout(hoverTimerRef.current);
        setShowReactions(false);
      }}
    >
      {!msg.isSelf && (
        <div className="cx-avatar-sm">{msg.sender?.[0]?.toUpperCase() || "?"}</div>
      )}
      <div className="cx-bubble-group">
        {!msg.isSelf && <div className="cx-bubble-sender">{msg.sender}</div>}

        <div className="cx-bubble-with-reaction">
          <div className={`cx-bubble${msg.isSelf ? " cx-bubble--self" : " cx-bubble--other"}`}>
            {msg.text}
          </div>

          {/* Reaction picker — appears on hover */}
          {showReactions && (
            <div className={`cx-reaction-picker${msg.isSelf ? " cx-reaction-picker--self" : ""}`}>
              {REACTION_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  className={`cx-reaction-btn${myReaction === emoji ? " cx-reaction-btn--active" : ""}`}
                  onClick={() => handleReactionClick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reactions row — my reaction + partner reaction */}
        {(myReaction || theirReaction) && (
          <div className={`cx-reaction-display${msg.isSelf ? " cx-reaction-display--self" : ""}`}>
            {myReaction && (
              <span
                className="cx-reaction-emoji cx-reaction-emoji--mine"
                title="Your reaction"
                onClick={() => handleReactionClick(myReaction)}
              >
                {myReaction}
              </span>
            )}
            {theirReaction && theirReaction !== myReaction && (
              <span
                className="cx-reaction-emoji cx-reaction-emoji--theirs"
                title={`${partnerNickname}'s reaction`}
              >
                {theirReaction}
              </span>
            )}
            {theirReaction && theirReaction === myReaction && (
              <span className="cx-reaction-emoji cx-reaction-emoji--both" title="Both reacted">
                {myReaction} ×2
              </span>
            )}
          </div>
        )}

        <div className={`cx-bubble-time${msg.isSelf ? " cx-bubble-time--self" : ""}`}>
          {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
function Modal({ children, onClose }) {
  return (
    <div className="cx-modal-overlay" onClick={onClose}>
      <div className="cx-modal" onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}


export default Chattr;